// /exams: the exams assigned to me and the ones I set (RFC-048 "The Exams
// page", byj-1wv.56.5), as /classes lists classes — rows under "Exams
// assigned to me" first, then "Exams I set", both headed, the empty one
// saying so; a new exam a dialog from the page's head, and how exams work
// beside it (byj-1wv.51.12). An exam is made, checked and published from
// the instructor's machine, where its hidden part is; here it is followed.
import { useCallback, useEffect, useState } from "react";
import { api, type BankItem, type ExamAssignmentView, type ExamSummary } from "../api";
import { AuthorCommands } from "../components/CommandLine";
import { useConfirm } from "../components/Confirm";
import { ExamGuideButton } from "../components/ExamGuide";
import { FormDialog } from "../components/FormDialog";
import { Link } from "../components/Nav";
import { Page, PageHead } from "../components/Page";
import { RowMenu } from "../components/RowMenu";
import { EmptyRows, Grouped } from "../components/Rows";
import { windowState } from "../exams";
import { useNow } from "../now";
import { navigate } from "../router";
import { t, whenMinute, word, phrases } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export const authorCommands = ["byj exam validate KIT", "byj exam calibrate KIT", "byj exam publish KIT --class CODE"];

/** An item's difficulty as a band, as byj reckons it: measured once sat, else authored. */
export const bandOf = (d?: BankItem["item"]["difficulty"]) =>
  d?.measured != null ? (d.measured >= 0.7 ? "easy" : d.measured < 0.4 ? "hard" : "medium") : d?.authored != null ? (d.authored <= 2 ? "easy" : d.authored >= 4 ? "hard" : "medium") : "medium";

/**
 * The instructor's bank (RFC-055): the items they published, each with its
 * family, band, minutes, what it measures, its status and how much it has
 * been exposed — drawn into papers on their machine, where its hidden part
 * is. Nothing when they have no item.
 */
export function Bank({ load = api.examBank }: { load?: typeof api.examBank }) {
  const [items, setItems] = useState<BankItem[] | null>(null);
  useEffect(() => {
    load().then(
      (b) => setItems(b.items),
      () => setItems([]),
    );
  }, [load]);
  if (!items || items.length === 0) return null;
  return (
    <section className="exam-section stack" aria-labelledby="exam-bank" data-testid="exam-bank">
      <h2 id="exam-bank" className="rows-head flush">
        {t("exams:bankTitle")} · {items.length}
      </h2>
      <p className="muted small flush">{t("exams:bankLead")}</p>
      <div className="doc-table exam-table" tabIndex={0}>
        <table>
          <thead>
            <tr>
              <th>{t("exams:bankItem")}</th>
              <th>{t("exams:bankFamily")}</th>
              <th>{t("exams:bankBand")}</th>
              <th>{t("exams:bankMeasures")}</th>
              <th>{t("exams:bankExposure")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id}>
                <td>
                  <Link to={`/e/${encodeURIComponent(x.exam_id)}`}>
                    <code>{x.id}</code>
                  </Link>{" "}
                  <span className="muted small">v{x.version}</span>
                  {x.item.status && <span className="chip">{x.item.status}</span>}
                </td>
                <td>{x.item.family}</td>
                <td>
                  {bandOf(x.item.difficulty)}
                  {x.item.minutes != null && <span className="muted small"> · {t("exams:bankMinutes", { m: x.item.minutes })}</span>}
                </td>
                <td className="small">
                  {Object.entries(x.item.competencies)
                    .map(([k, v]) => `${word("exams:rubricPart", k) ?? k} ${Math.round(v * 100)}%`)
                    .join(" · ")}
                </td>
                <td className="small">{t("exams:bankExposureLine", { papers: x.exposure.papers, classes: x.exposure.assignments, sat: x.exposure.sat })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AuthorCommands commands={["byj exam bank", "byj exam assemble BLUEPRINT --seed N"]} />
    </section>
  );
}

/** A row of the page: an exam assigned to me, or one I set. */
type ExamItem = { kind: "assigned"; a: ExamAssignmentView } | { kind: "teaching"; e: ExamSummary };

/** An exam assigned to me: its class and its window, where the window stands, to its assignment's page; its class from its ⋯. */
function AssignedRow({ a, now }: { a: ExamAssignmentView; now: number }) {
  const state = windowState(a, now);
  return (
    <li className="share-row exam-row is-live">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        exm
      </span>
      <div className="row-main">
        <Link to={`/ea/${encodeURIComponent(a.id)}`} className="row-title">
          {a.exam.title}
        </Link>
        <div className="row-sub">
          {t("exams:examClass", { name: a.class.name })}
          {a.opens_at && <> · {t("exams:opensAt", { w: whenMinute(a.opens_at) })}</>}
          {a.closes_at && <> · {t("exams:closesAt", { w: whenMinute(a.closes_at) })}</>}
        </div>
      </div>
      <div className="row-meta">
        {(a.released ?? 0) > 0 && (
          // a grade released is said where the student looks first, not only on the exam's page (byj-1wv.135)
          <span className="chip chip-ok" data-testid="exam-released">
            {t("exams:gradeReleased", { count: a.released })}
          </span>
        )}
        <span className={`chip ${state === "open" ? "chip-live" : ""}`} data-testid="exam-window">
          {word("exams:examWindowState", state)}
        </span>
      </div>
      <RowMenu label={t("shares:moreFor", { title: a.exam.title })} items={[{ label: t("exams:openTheClass"), onSelect: () => navigate(`/c/${encodeURIComponent(a.class.id)}`) }]} />
    </li>
  );
}

/** An exam I set: its versions and classes, and how many hand-ins wait to be graded; deleting it behind its ⋯, as a class's is. */
function TeachingRow({ e, onDelete }: { e: ExamSummary; onDelete: (e: ExamSummary) => void }) {
  return (
    <li className="share-row exam-row is-live">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        exm
      </span>
      <div className="row-main">
        <Link to={`/e/${encodeURIComponent(e.id)}`} className="row-title">
          {e.title}
        </Link>
        <div className="row-sub">{phrases.examTeachingRow(e.versions, e.assignments)}</div>
      </div>
      <div className="row-meta">
        {e.bank && <span className="chip" data-testid="exam-from-bank">{t("exams:fromBank")}</span>}
        {e.queued > 0 && <span className="chip chip-warn">{t("exams:toGrade", { n: e.queued })}</span>}
      </div>
      <RowMenu
        label={t("shares:moreFor", { title: e.title })}
        items={[
          { label: t("exams:openExam"), onSelect: () => navigate(`/e/${encodeURIComponent(e.id)}`) },
          { label: t("exams:deleteExam"), onSelect: () => onDelete(e), danger: true },
        ]}
      />
    </li>
  );
}

/**
 * A new exam, a short form over the page (components/FormDialog): its title,
 * then on to its page to write it, where byj pulls it into a kit
 * (byj-1wv.56.11).
 */
function NewExamDialog({ create, onClose }: { create: typeof api.createExam; onClose: () => void }) {
  const [title, setTitle] = useState("");
  return (
    <FormDialog
      id="new-exam-dialog"
      title={t("exams:newExamTitle")}
      submitLabel={t("exams:newExamCreate")}
      canSubmit={title.trim() !== ""}
      onSubmit={async () => {
        const x = await create(title.trim());
        navigate(`/e/${encodeURIComponent(x.id)}`);
      }}
      onClose={onClose}
      testid="new-exam"
    >
      <label className="field">
        {t("exams:newExamLabel")}
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("exams:newExamPlaceholder")} maxLength={200} />
      </label>
      <p className="muted small flush">{t("exams:newExamNote")}</p>
    </FormDialog>
  );
}

export function Exams({ load = api.exams, create = api.createExam, remove = api.deleteExam, bank = api.examBank, own = true }: {
  load?: typeof api.exams;
  create?: typeof api.createExam;
  remove?: typeof api.deleteExam;
  bank?: typeof api.examBank;
  /** The person authors exams here; a third party's people do not, and are shown none of it (byj-1wv.117.10). */
  own?: boolean;
}) {
  const [data, setData] = useState<{ teaching: ExamSummary[]; assigned: ExamAssignmentView[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  // deleted from its row, asked first in the exam page's own words (byj-1wv.56.14)
  const deleteExam = async (e: ExamSummary) => {
    if (!(await confirm(phrases.deleteExamConfirm(e.title, e.assignments), { action: t("exams:deleteExam"), danger: true }))) return;
    setError(null);
    try {
      await remove(e.id);
      setData((d) => (d ? { ...d, teaching: d.teaching.filter((x) => x.id !== e.id) } : d));
    } catch (err) {
      setError(errText(err));
    }
  };
  const [creating, setCreating] = useState(false);
  // where each window stands, said again while the page is open
  const now = useNow();
  const fetch = useCallback(async () => {
    setError(null);
    try {
      setData(await load());
    } catch (err) {
      setError(errText(err));
    }
  }, [load]);
  useEffect(() => {
    void fetch();
  }, [fetch]);

  return (
    <Page className="exams-page">
      {confirmDialog}
      <PageHead
        title={t("exams:examsTitle")}
        actions={
          <>
            <ExamGuideButton sitting />
            {/* named in aria-controls while open, so the dialog gives the focus back to it */}
            {own && (
              <button className="primary" aria-haspopup="dialog" aria-expanded={creating} aria-controls={creating ? "new-exam-dialog" : undefined} onClick={() => setCreating(true)}>
                {t("exams:newExam")}
              </button>
            )}
          </>
        }
      />
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void fetch()}>{t("common:retry")}</button>
        </p>
      )}
      {data === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <div className="rows-panel class-groups" data-testid="exam-list">
          {/* a student sees first what is set for them */}
          <Grouped<ExamItem>
            id="exams"
            headed
            groups={[
              { title: t("exams:examsAssigned"), key: "assigned", testid: "exams-assigned", items: data.assigned.map((a) => ({ kind: "assigned" as const, a })), none: <EmptyRows text={t("exams:noExamsAssigned")} /> },
              {
                title: t("exams:examsTeaching"),
                key: "teaching",
                testid: "exams-teaching",
                items: data.teaching.map((e) => ({ kind: "teaching" as const, e })),
                none: (
                  <div className="empty-card" data-testid="no-exams-teaching">
                    <p className="muted small">{t("exams:noExamsTeaching")}</p>
                    <AuthorCommands commands={authorCommands} />
                  </div>
                ),
              },
              // one who authors nothing here and teaches nothing is shown no group of it
            ].filter((g) => own || g.key !== "teaching" || data.teaching.length > 0)}
            row={(x) => (x.kind === "assigned" ? <AssignedRow key={x.a.id} a={x.a} now={now} /> : <TeachingRow key={x.e.id} e={x.e} onDelete={(e) => void deleteExam(e)} />)}
          />
        </div>
      )}
      {own && data && data.teaching.length > 0 && <Bank load={bank} />}
      {creating && <NewExamDialog create={create} onClose={() => setCreating(false)} />}
    </Page>
  );
}
